/**
 * Интеграция с Telegram Mini App.
 *
 * Модуль намеренно не зависит ни от api, ни от сторов: его дёргают из
 * `main.tsx` до первого запроса, и он же обслуживает хранение refresh-токена,
 * которым пользуется api. Обратная зависимость дала бы цикл.
 *
 * SDK Telegram подключён скриптом в index.html — из него берётся глобальный
 * `window.Telegram.WebApp`. Пакета типов у него нет, поэтому описываем ровно
 * ту часть поверхности, которой пользуемся: чужие типы всё равно отстают от
 * версии скрипта.
 */

// ──────────────────────────── Поверхность SDK ────────────────────────────

interface TelegramThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
  header_bg_color?: string;
  accent_text_color?: string;
  section_bg_color?: string;
  section_separator_color?: string;
  subtitle_text_color?: string;
  destructive_text_color?: string;
}

interface TelegramCloudStorage {
  getItem(key: string, callback: (error: string | null, value?: string) => void): void;
  setItem(
    key: string,
    value: string,
    callback?: (error: string | null, stored?: boolean) => void,
  ): void;
  removeItem(
    key: string,
    callback?: (error: string | null, removed?: boolean) => void,
  ): void;
}

interface TelegramBackButton {
  isVisible: boolean;
  show(): void;
  hide(): void;
  onClick(handler: () => void): void;
  offClick(handler: () => void): void;
}

interface TelegramWebApp {
  initData: string;
  version: string;
  platform: string;
  colorScheme: 'light' | 'dark';
  themeParams: TelegramThemeParams;
  isExpanded: boolean;
  viewportHeight: number;
  viewportStableHeight: number;
  BackButton: TelegramBackButton;
  CloudStorage?: TelegramCloudStorage;
  ready(): void;
  expand(): void;
  close(): void;
  onEvent(event: string, handler: () => void): void;
  offEvent(event: string, handler: () => void): void;
  setHeaderColor?(color: string): void;
  setBackgroundColor?(color: string): void;
  disableVerticalSwipes?(): void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

function webApp(): TelegramWebApp | null {
  const app = window.Telegram?.WebApp;
  // initData пустая — страницу открыли по прямой ссылке в браузере, где
  // скрипт Telegram всё равно создаёт объект. Это НЕ Mini App: там нет ни
  // CloudStorage, ни BackButton, и лечить его функциями SDK нельзя.
  if (!app || typeof app.initData !== 'string' || app.initData.length === 0) return null;
  return app;
}

/** Панель открыта внутри Telegram. Проверяется всюду, где расходится поведение. */
export function isMiniApp(): boolean {
  return webApp() !== null;
}

/** Сырой initData для проверки подписи на сервере, если он понадобится. */
export function miniAppInitData(): string | null {
  return webApp()?.initData ?? null;
}

// ──────────────────────────── Инициализация ────────────────────────────

let initialized = false;

/**
 * Поднять Mini App: сообщить о готовности, развернуть на весь экран, перенести
 * тему Telegram в CSS-переменные.
 *
 * Возвращает false в обычном браузере — вызывающему не нужно самому проверять
 * isMiniApp().
 */
export function initTelegram(): boolean {
  const app = webApp();
  if (!app) return false;
  if (initialized) return true;
  initialized = true;

  app.ready();
  // Без expand() Mini App открывается «шторкой» на половину экрана, и список
  // диалогов помещается штук на пять.
  app.expand();
  // Свайп вниз закрывает Mini App — в чате им же прокручивают ленту.
  app.disableVerticalSwipes?.();

  applyTelegramTheme(app);
  applyViewport(app);

  const onTheme = () => applyTelegramTheme(app);
  const onViewport = () => applyViewport(app);
  app.onEvent('themeChanged', onTheme);
  app.onEvent('viewportChanged', onViewport);

  document.documentElement.classList.add('is-miniapp');
  return true;
}

/**
 * Тема Telegram → наши токены.
 *
 * Переопределяем только акцент и фоны: остальная палитра общая с вебом, и
 * подменять её целиком означало бы получить у каждого оператора свой дизайн.
 * Акцент берётся из button_color — это цвет, который клиент видит в самом
 * Telegram, и панель обязана с ним совпадать.
 */
function applyTelegramTheme(app: TelegramWebApp): void {
  const root = document.documentElement;
  const params = app.themeParams;

  root.setAttribute('data-theme', app.colorScheme === 'dark' ? 'dark' : 'light');

  if (params.button_color) {
    root.style.setProperty('--accent', params.button_color);
    root.style.setProperty('--accent-soft', hexToSoft(params.button_color));
    // --accent-strong в CSS читают только .btn:hover и .btn-primary:hover.
    // Тот же цвет, что у --accent, сделал бы наведение неотличимым от покоя, а
    // оставить наш фиолетовый нельзя: он разошёлся бы с темой Mini App.
    root.style.setProperty('--accent-strong', darken(params.button_color, 0.88));
    root.style.setProperty('--bubble-out', params.button_color);
  }
  if (params.button_text_color) {
    root.style.setProperty('--bubble-out-text', params.button_text_color);
  }
  if (params.bg_color) {
    root.style.setProperty('--bg-elev', params.bg_color);
    app.setBackgroundColor?.(params.bg_color);
    app.setHeaderColor?.(params.header_bg_color ?? params.bg_color);
  }
  if (params.secondary_bg_color) {
    root.style.setProperty('--bg', params.secondary_bg_color);
    root.style.setProperty('--bg-soft', params.secondary_bg_color);
    root.style.setProperty('--chat-bg', params.secondary_bg_color);
  }
}

/**
 * Высота вьюпорта Telegram в переменной.
 *
 * `100vh` в webview врёт: клавиатура и шапка Telegram съедают часть экрана, а
 * vh про это не знает — композер уезжает под клавиатуру. Считаем от
 * viewportStableHeight, который SDK пересчитывает сам.
 */
function applyViewport(app: TelegramWebApp): void {
  const height = app.viewportStableHeight || app.viewportHeight;
  if (height > 0) {
    document.documentElement.style.setProperty('--tg-viewport', `${height}px`);
  }
}

/** #rrggbb → rgba(...,.1) для подложек. Непарсимое значение отдаём как есть. */
function hexToSoft(color: string): string {
  const rgb = parseHex(color);
  if (rgb === null) return color;
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.12)`;
}

/**
 * Притемнение по каналам для состояния наведения.
 *
 * Умножение каналов, а не смешивание с чёрным через CSS: значение уходит в
 * inline-стиль корня, где color-mix пришлось бы пересобирать строкой, и любая
 * опечатка в ней молча оставила бы переменную невалидной.
 */
function darken(color: string, factor: number): string {
  const rgb = parseHex(color);
  if (rgb === null) return color;
  // Без деструктуризации через map: она теряет длину кортежа, и каналы
  // становятся number|undefined — в CSS уехало бы «rgb(undefined, …)».
  const scale = (channel: number): number => Math.round(channel * factor);
  return `rgb(${scale(rgb[0])}, ${scale(rgb[1])}, ${scale(rgb[2])})`;
}

function parseHex(color: string): [number, number, number] | null {
  const match = /^#?([\da-f]{6})$/i.exec(color.trim());
  if (!match?.[1]) return null;
  const int = Number.parseInt(match[1], 16);
  return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
}

// ──────────────────────────── Кнопка «назад» ────────────────────────────

let backHandler: (() => void) | null = null;

/**
 * Системная кнопка «назад» вместо навигации браузера.
 *
 * В webview кнопки браузера нет вовсе, а свайп назад закрывает Mini App
 * целиком, а не возвращает к списку диалогов. Обработчик ровно один: Telegram
 * не снимает подписки при повторном onClick, и два вызова дали бы двойной шаг
 * назад по истории.
 */
export function setBackButton(handler: (() => void) | null): void {
  const app = webApp();
  if (!app) return;

  if (backHandler) {
    app.BackButton.offClick(backHandler);
    backHandler = null;
  }

  if (handler) {
    backHandler = handler;
    app.BackButton.onClick(handler);
    app.BackButton.show();
  } else {
    app.BackButton.hide();
  }
}

// ──────────────────────────── CloudStorage ────────────────────────────

/** Ключ сессии в CloudStorage. Алфавит ключей у Telegram ограничен A-Za-z0-9_-. */
export const TG_REFRESH_KEY = 'support_refresh';

/**
 * Сессия Mini App.
 *
 * Почему не cookie: в webview Telegram сторонние cookie ненадёжны — на iOS их
 * режет ITP, и оператор разлогинивается на каждом открытии. Почему не
 * localStorage: он общий на весь домен и переживает выход, а CloudStorage
 * привязан к аккаунту Telegram и уезжает вместе с ним.
 *
 * Все методы безопасны вне Mini App: возвращают null / no-op, чтобы вызывающий
 * не городил ветвление вокруг каждого обращения.
 */
export const tgStorage = {
  async get(key: string): Promise<string | null> {
    const storage = webApp()?.CloudStorage;
    if (!storage) return null;
    return new Promise((resolve) => {
      try {
        storage.getItem(key, (error, value) => {
          resolve(error || !value ? null : value);
        });
      } catch {
        // Старый клиент Telegram без CloudStorage: вход просто попросят заново.
        resolve(null);
      }
    });
  },

  async set(key: string, value: string): Promise<boolean> {
    const storage = webApp()?.CloudStorage;
    if (!storage) return false;
    return new Promise((resolve) => {
      try {
        storage.setItem(key, value, (error, stored) => {
          resolve(!error && stored !== false);
        });
      } catch {
        resolve(false);
      }
    });
  },

  async remove(key: string): Promise<void> {
    const storage = webApp()?.CloudStorage;
    if (!storage) return;
    return new Promise((resolve) => {
      try {
        storage.removeItem(key, () => resolve());
      } catch {
        resolve();
      }
    });
  },
};
